import React, { useState } from 'react';

import { StatusBadge } from './status-badge';
import { Message, isUserQuestion } from './message';
import { useConvosContext } from './convos';
import { AttributesDialog } from './attributes-dialog';
import { DivButton } from '../ui/div-button';
import { StreamBadge } from './stream-badge';

import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Item, ItemContent, ItemDescription, ItemTitle } from '@/components/ui/item';
import { cn } from '@/lib/utils';
import { formatDistance } from '@/lib/formatDate';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/index';

interface Source {
  name: string;
  color?: string;
}

interface Props {
  locale?: string; // only if not using en-US. For formatting dates (overrides context locale)
  question: string; // the initial query
  timestamp: Date; // created_at date of the conversation thread
  location?: string; // location of the user
  source?: Source; // source of the conversation, i.e. channel name and color
  status?: React.ComponentProps<typeof StatusBadge>['variant'];
  statusMessage?: string; // status of the conversation - Answered | Article not found | Partial | Unanswered, etc
  hasAttributes?: boolean; // whether the conversation has attributes
  onClick?: () => void; // handler to fetch messages when accordion is clicked
  score?: number; // score of the conversation
}

export type ConvoProps = React.ComponentPropsWithRef<typeof AccordionItem> & Props;

type OpenDialog = 'attributes' | 'feedback' | null;

type EnrichedStatusBadgeProps = Pick<ConvoProps, 'score' | 'status' | 'statusMessage'>;
const EnrichedStatusBadge = ({ score, status, statusMessage }: EnrichedStatusBadgeProps) => {
  return score ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <DivButton variant="ghost" size="sm" className="p-0 h-[26px]">
          <StatusBadge variant={status} suffix={score}>
            {statusMessage}
          </StatusBadge>
        </DivButton>
      </TooltipTrigger>
      <TooltipContent>
        <p>Conversation score: {score}</p>
      </TooltipContent>
    </Tooltip>
  ) : (
    <StatusBadge variant={status} suffix={score}>
      {statusMessage}
    </StatusBadge>
  );
};

/**
 * Convo component - A single conversation item in an accordion.
 * Displays conversation metadata and messages, with support for lazy loading.
 *
 * This is a presentational component - it receives messages and loading state as props
 * rather than managing fetching logic itself.
 */
export function Convo({
  locale: localeOverride,
  value: queryId,
  question,
  timestamp,
  location,
  source,
  status,
  statusMessage,
  hasAttributes,
  onClick,
  className,
  score,
  ...props
}: ConvoProps) {
  const { getMessages, isLoading: isLoadingFromContext, locale: contextLocale } = useConvosContext();

  // Dialog state management
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const [openFeedbackMessageId, setOpenFeedbackMessageId] = useState<string | null>(null);

  const handleDialogChange = (dialogType: OpenDialog, messageId?: string) => (isOpen: boolean) => {
    if (isOpen) {
      setOpenDialog(dialogType);
      if (messageId) setOpenFeedbackMessageId(messageId);
    } else {
      setOpenDialog(null);
      setOpenFeedbackMessageId(null);
    }
  };

  const isAnyDialogOpen = openDialog !== null;

  // Use locale override if provided, otherwise use context locale
  const locale = localeOverride || contextLocale;

  // Get messages and loading state from context
  const messages = getMessages(queryId);
  const isLoading = isLoadingFromContext(queryId);

  // Format date
  const formattedDateRelative = formatDistance(timestamp, locale);

  // Create description string: "Nov 1, 2025, 11:00 AM • United States"
  // Filters out undefined/null values and joins with bullet separator
  const description = [formattedDateRelative, location].filter(Boolean).join(' • ');

  const handleAccordionClick = (e: React.MouseEvent) => {
    // Prevent accordion from toggling if click originated from within a dialog
    if ((e.target as HTMLElement).closest('[role="dialog"]')) {
      e.preventDefault();
      e.stopPropagation();

      return;
    }
    onClick?.();
  };

  return (
    <AccordionItem
      value={queryId}
      className={cn(
        'border border-dark-200 hover:border-dark-300 rounded-lg bg-dark-100 [&[data-state="open"]]:bg-surface [&[data-state="open"]]:border-dark-100',
        { 'shadow-md shadow-dark-300': isAnyDialogOpen },
        className,
      )}
      {...props}
    >
      <AccordionTrigger
        className="flex-row-reverse px-4 gap-1 items-center [&[data-state=open]]:px-6"
        onClick={handleAccordionClick}
      >
        <Item size="sm" className="flex-1 p-0 justify-between">
          <ItemContent className="max-w-[400px]">
            <ItemTitle className="text-base text-default">{question}</ItemTitle>
            <ItemDescription className="text-xs text-default flex items-center gap-1">
              {description}
              {source && <StreamBadge streamName={source.name} dotColor={source.color} />}
            </ItemDescription>
          </ItemContent>
          <div className="flex items-center gap-0">
            {hasAttributes && (
              <AttributesDialog
                conversationId={queryId}
                open={openDialog === 'attributes'}
                onOpenChange={handleDialogChange('attributes')}
              />
            )}
            {/* Add tooltip to say "Conversation score", only when there is a score */}
            {status && <EnrichedStatusBadge score={score} status={status} statusMessage={statusMessage} />}
          </div>
        </Item>
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-4 text-balance px-5">
        {isLoading ? (
          <div className="text-center text-subtle py-4">Loading messages...</div>
        ) : (
          <ul className="list-none space-y-4">
            {messages.map((message, idx) => {
              const userMsg = isUserQuestion(message) ? message : null;
              const attachments = userMsg?.attachments?.length ? userMsg.attachments : null;

              // First message text is already shown in the accordion header.
              // Skip unless it has attachments to display.
              if (idx === 0) {
                return attachments ? (
                  <Message
                    key={message.id}
                    {...{ ...message, onlyShowAttachments: true }}
                    isFeedbackDialogOpen={openDialog === 'feedback' && openFeedbackMessageId === message.id}
                    onFeedbackDialogChange={handleDialogChange('feedback', message.id)}
                  />
                ) : null;
              }

              return (
                <Message
                  key={message.id}
                  {...message}
                  isFeedbackDialogOpen={openDialog === 'feedback' && openFeedbackMessageId === message.id}
                  onFeedbackDialogChange={handleDialogChange('feedback', message.id)}
                />
              );
            })}
          </ul>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
